import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import { 
  UserCheck, AlertCircle, CheckCircle, Clock, Search, Filter, AlertTriangle, 
  UserX, Upload, FileText, Calendar, Eye, Trash2, Download, Plus, User
} from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "../lib/queryClient";
import { apiRequest } from "../lib/queryClient";
import { useToast } from "../hooks/use-toast";
import { format } from "date-fns";

interface Visitor {
  id: number;
  fullName: string;
  identityNumber: string;
  idType: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  accessLevel: string;
  complianceStatus: string;
  isRevoked: boolean;
  revokedUntil: Date | null;
  revokeReason: string | null;
  lastComplianceCheck: Date | null;
  createdAt: Date;
}

interface VisitorDocument {
  id: string;
  visitorId: number;
  documentType: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  expiryDate: Date;
  issuedDate: Date | null;
  status: string;
  ocrStatus: string;
  ocrData: any;
  verificationStatus: string;
  uploadedBy: string;
  isCurrentVersion: boolean;
  createdAt: Date;
}

interface ComplianceCheckResult {
  visitor: Visitor;
  documents: VisitorDocument[];
  complianceStatus: string;
  expiringDocuments: VisitorDocument[];
  expiredDocuments: VisitorDocument[];
}

export default function VisitorCompliance() {
  const [selectedVisitor, setSelectedVisitor] = useState<Visitor | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [showAddVisitor, setShowAddVisitor] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [expiryDate, setExpiryDate] = useState("");
  const [issuedDate, setIssuedDate] = useState("");
  const [revokeReason, setRevokeReason] = useState("");
  const [revokePeriod, setRevokePeriod] = useState("");
  const [revokeUnit, setRevokeUnit] = useState("");
  const [selectedVisitorForDetails, setSelectedVisitorForDetails] = useState<Visitor | null>(null);
  const [showVisitorDetails, setShowVisitorDetails] = useState(false);
  const [newVisitorData, setNewVisitorData] = useState({
    fullName: '',
    identityNumber: '',
    idType: 'id',
    email: '',
    phone: '',
    company: '',
    accessLevel: 'basic'
  });

  // Delete document mutation
  const deleteDocumentMutation = useMutation({
    mutationFn: (documentId: string) => apiRequest(`/api/visitor-documents/${documentId}`, {
      method: 'DELETE'
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/visitors'] });
      queryClient.invalidateQueries({ queryKey: ['/api/visitors', selectedVisitorForDetails?.id, 'documents'] });
      queryClient.invalidateQueries({ queryKey: ['/api/documents/expiring'] });
      queryClient.invalidateQueries({ queryKey: ['/api/documents/expired'] });
      toast({ title: "Success", description: "Document deleted successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete document", variant: "destructive" });
    }
  });

  // Compliance recheck mutation
  const recheckComplianceMutation = useMutation({
    mutationFn: (visitorId: number) => apiRequest(`/api/visitors/${visitorId}/compliance/recheck`, {
      method: 'POST'
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/visitors'] });
      queryClient.invalidateQueries({ queryKey: ['/api/visitors', selectedVisitorForDetails?.id, 'documents'] });
      toast({ title: "Success", description: "Compliance status updated" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to recheck compliance", variant: "destructive" });
    }
  });

  // Document handlers
  const handleViewDocument = (doc: VisitorDocument) => {
    window.open(doc.fileUrl, '_blank');
  };

  const handleDownloadDocument = (doc: VisitorDocument) => {
    window.open(`/api/visitor-documents/${doc.id}/download`, '_blank');
  };

  const handleDeleteDocument = (doc: VisitorDocument) => {
    if (confirm(`Are you sure you want to delete ${doc.fileName}?`)) {
      deleteDocumentMutation.mutate(doc.id);
    }
  };
  
  const { toast } = useToast();

  // Fetch visitors
  const { data: visitors = [], isLoading: visitorsLoading, error: visitorsError } = useQuery({
    queryKey: ['/api/visitors'],
    select: (data: Visitor[]) => data || []
  });

  // Fetch visitor documents for selected visitor
  const { data: visitorDocuments = [], isLoading: documentsLoading } = useQuery({
    queryKey: ['/api/visitors', selectedVisitorForDetails?.id, 'documents'],
    enabled: !!selectedVisitorForDetails,
    select: (data: VisitorDocument[]) => data || []
  });

  // Fetch expiring documents
  const { data: expiringDocuments = [] } = useQuery({
    queryKey: ['/api/documents/expiring'],
    select: (data: VisitorDocument[]) => data || []
  });

  // Fetch expired documents
  const { data: expiredDocuments = [] } = useQuery({
    queryKey: ['/api/documents/expired'],
    select: (data: VisitorDocument[]) => data || []
  });

  // Create visitor mutation
  const createVisitorMutation = useMutation({
    mutationFn: (newVisitor: Partial<Visitor>) => apiRequest('/api/visitors', {
      method: 'POST',
      body: JSON.stringify(newVisitor)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/visitors'] });
      setShowAddVisitor(false);
      setNewVisitorData({
        fullName: '',
        identityNumber: '',
        idType: 'id',
        email: '',
        phone: '',
        company: '',
        accessLevel: 'basic'
      });
      toast({ title: "Success", description: "Visitor created successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create visitor", variant: "destructive" });
    }
  });

  // Upload document mutation with real file upload
  const uploadDocumentMutation = useMutation({
    mutationFn: async ({ visitorId, formData }: { visitorId: number, formData: FormData }) => {
      const response = await fetch(`/api/visitors/${visitorId}/documents/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/visitors'] });
      queryClient.invalidateQueries({ queryKey: ['/api/visitors', selectedVisitor?.id, 'documents'] });
      queryClient.invalidateQueries({ queryKey: ['/api/documents/expiring'] });
      queryClient.invalidateQueries({ queryKey: ['/api/documents/expired'] });
      setShowDocumentUpload(false);
      setDocumentFile(null);
      setSelectedDocumentType("");
      setExpiryDate("");
      setIssuedDate("");
      toast({ title: "Success", description: "Document uploaded successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to upload document", variant: "destructive" });
    }
  });

  // Revoke access mutation
  const revokeAccessMutation = useMutation({
    mutationFn: ({ visitorId, revokedUntil, reason }: { visitorId: number, revokedUntil: string, reason: string }) =>
      apiRequest(`/api/visitors/${visitorId}/revoke`, {
        method: 'POST',
        body: JSON.stringify({ revokedUntil, reason })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/visitors'] });
      setSelectedVisitor(null);
      setRevokeReason("");
      setRevokePeriod("");
      setRevokeUnit("");
      toast({ title: "Success", description: "Access revoked successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to revoke access", variant: "destructive" });
    }
  });

  // Reinstate access mutation
  const reinstateAccessMutation = useMutation({
    mutationFn: (visitorId: number) => apiRequest(`/api/visitors/${visitorId}/reinstate`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/visitors'] });
      toast({ title: "Success", description: "Access reinstated successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to reinstate access", variant: "destructive" });
    }
  });

  // Filter visitors based on search and status
  const filteredVisitors = visitors.filter(visitor => {
    const matchesSearch = !searchTerm || 
      visitor.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      visitor.identityNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      visitor.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || statusFilter === 'all' || visitor.complianceStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleRevoke = () => {
    if (selectedVisitor && revokePeriod && revokeUnit && revokeReason) {
      const period = parseInt(revokePeriod);
      const currentDate = new Date();
      let revokeUntil = new Date(currentDate);
      
      switch (revokeUnit) {
        case "days":
          revokeUntil.setDate(currentDate.getDate() + period);
          break;
        case "weeks":
          revokeUntil.setDate(currentDate.getDate() + (period * 7));
          break;
        case "months":
          revokeUntil.setMonth(currentDate.getMonth() + period);
          break;
        case "years":
          revokeUntil.setFullYear(currentDate.getFullYear() + period);
          break;
      }
      
      revokeAccessMutation.mutate({
        visitorId: selectedVisitor.id,
        revokedUntil: revokeUntil.toISOString(),
        reason: revokeReason
      });
    }
  };

  const handleDocumentUpload = () => {
    if (selectedVisitor && documentFile && selectedDocumentType && expiryDate) {
      const formData = new FormData();
      formData.append('document', documentFile);
      formData.append('documentType', selectedDocumentType);
      formData.append('expiryDate', expiryDate);
      if (issuedDate) {
        formData.append('issuedDate', issuedDate);
      }

      uploadDocumentMutation.mutate({
        visitorId: selectedVisitor.id,
        formData
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      'compliant': 'default',
      'expiring': 'secondary', 
      'non_compliant': 'destructive',
      'expired': 'destructive',
      'revoked': 'destructive',
      'pending': 'outline'
    };
    
    return (
      <Badge variant={variants[status as keyof typeof variants] || 'outline'}>
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'expiring':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'non_compliant':
      case 'expired':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'revoked':
        return <UserX className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  // Calculate summary stats
  const compliantCount = visitors.filter(v => v.complianceStatus === 'compliant').length;
  const expiringCount = visitors.filter(v => v.complianceStatus === 'expiring').length;
  const nonCompliantCount = visitors.filter(v => ['non_compliant', 'expired'].includes(v.complianceStatus)).length;
  const revokedCount = visitors.filter(v => v.isRevoked).length;

  if (visitorsError) {
    return (
      <div>
        <div className="space-y-6 p-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Error Loading Visitor Data</h3>
                <p className="text-muted-foreground mb-4">Unable to fetch visitor compliance information</p>
                <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['/api/visitors'] })}>
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Visitor Compliance</h2>
            <p className="text-muted-foreground">Monitor visitor documents and certification expiry dates</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowAddVisitor(true)}
              data-testid="button-add-visitor"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Visitor
            </Button>
            <Button 
              onClick={() => queryClient.invalidateQueries({ queryKey: ['/api/visitors'] })}
              data-testid="button-refresh"
            >
              <UserCheck className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search by name, ID, email..." 
                    className="pl-10"
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                    data-testid="input-search-visitors"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48" data-testid="select-status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="compliant">Compliant</SelectItem>
                  <SelectItem value="expiring">Expiring Soon</SelectItem>
                  <SelectItem value="non_compliant">Non-Compliant</SelectItem>
                  <SelectItem value="revoked">Revoked</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Expiring and Expired Documents Alerts */}
        {(expiringDocuments.length > 0 || expiredDocuments.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {expiredDocuments.length > 0 && (
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="text-red-700 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    Expired Documents ({expiredDocuments.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {expiredDocuments.slice(0, 5).map((doc) => (
                      <div key={doc.id} className="text-sm">
                        <p className="font-medium">{doc.fileName}</p>
                        <p className="text-red-600">Expired: {format(new Date(doc.expiryDate), 'MMM dd, yyyy')}</p>
                      </div>
                    ))}
                    {expiredDocuments.length > 5 && (
                      <p className="text-xs text-muted-foreground">+{expiredDocuments.length - 5} more...</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {expiringDocuments.length > 0 && (
              <Card className="border-yellow-200 bg-yellow-50">
                <CardHeader>
                  <CardTitle className="text-yellow-700 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Expiring Soon ({expiringDocuments.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {expiringDocuments.slice(0, 5).map((doc) => (
                      <div key={doc.id} className="text-sm">
                        <p className="font-medium">{doc.fileName}</p>
                        <p className="text-yellow-600">Expires: {format(new Date(doc.expiryDate), 'MMM dd, yyyy')}</p>
                      </div>
                    ))}
                    {expiringDocuments.length > 5 && (
                      <p className="text-xs text-muted-foreground">+{expiringDocuments.length - 5} more...</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-2xl font-bold text-green-600" data-testid="stat-compliant">
                    {visitorsLoading ? <Skeleton className="h-8 w-8" /> : compliantCount}
                  </p>
                  <p className="text-sm text-muted-foreground">Fully Compliant</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold text-yellow-600" data-testid="stat-expiring">
                    {visitorsLoading ? <Skeleton className="h-8 w-8" /> : expiringDocuments.length}
                  </p>
                  <p className="text-sm text-muted-foreground">Expiring Soon</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <div>
                  <p className="text-2xl font-bold text-red-600" data-testid="stat-non-compliant">
                    {visitorsLoading ? <Skeleton className="h-8 w-8" /> : expiredDocuments.length}
                  </p>
                  <p className="text-sm text-muted-foreground">Expired Docs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold text-blue-600" data-testid="stat-total">
                    {visitorsLoading ? <Skeleton className="h-8 w-8" /> : visitors.length}
                  </p>
                  <p className="text-sm text-muted-foreground">Total Visitors</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Compliance Documents Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Compliance Requirements Overview</CardTitle>
            <CardDescription>Required documents for site access with expiry tracking</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all-documents" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="all-documents">All Documents</TabsTrigger>
                <TabsTrigger value="by-type">By Document Type</TabsTrigger>
                <TabsTrigger value="expiry-alerts">Expiry Alerts</TabsTrigger>
              </TabsList>
              
              <TabsContent value="all-documents" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {['license', 'induction', 'pdp', 'medical', 'safety_cert', 'passport', 'id'].map((docType) => {
                    const typeDocuments = [...expiringDocuments, ...expiredDocuments].filter(doc => doc.documentType === docType);
                    const expiredCount = typeDocuments.filter(doc => new Date(doc.expiryDate) < new Date()).length;
                    const expiringCount = typeDocuments.filter(doc => {
                      const expiry = new Date(doc.expiryDate);
                      const now = new Date();
                      const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                      return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
                    }).length;
                    
                    return (
                      <Card key={docType} className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold capitalize">{docType.replace('_', ' ')}</h4>
                            <p className="text-sm text-muted-foreground">{typeDocuments.length} documents</p>
                          </div>
                          <div className="text-right">
                            {expiredCount > 0 && (
                              <Badge variant="destructive" className="mr-1">{expiredCount} expired</Badge>
                            )}
                            {expiringCount > 0 && (
                              <Badge variant="secondary">{expiringCount} expiring</Badge>
                            )}
                            {expiredCount === 0 && expiringCount === 0 && (
                              <Badge variant="default">All current</Badge>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>
              
              <TabsContent value="by-type" className="space-y-4">
                <div className="space-y-6">
                  {['license', 'induction', 'pdp', 'medical', 'safety_cert', 'passport', 'id'].map((docType) => {
                    const typeDocuments = [...expiringDocuments, ...expiredDocuments, ...visitorDocuments]
                      .filter(doc => doc.documentType === docType)
                      .sort((a, b) => new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime());
                    
                    if (typeDocuments.length === 0) return null;
                    
                    return (
                      <Card key={docType}>
                        <CardHeader>
                          <CardTitle className="capitalize flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            {docType.replace('_', ' ')} Documents
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Visitor</TableHead>
                                <TableHead>File Name</TableHead>
                                <TableHead>Expiry Date</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {typeDocuments.slice(0, 5).map((doc) => {
                                const visitor = visitors.find(v => v.id === doc.visitorId);
                                const isExpired = new Date(doc.expiryDate) < new Date();
                                const daysUntilExpiry = Math.ceil((new Date(doc.expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                                
                                return (
                                  <TableRow key={doc.id}>
                                    <TableCell>{visitor?.fullName || 'Unknown'}</TableCell>
                                    <TableCell className="font-mono text-sm">{doc.fileName}</TableCell>
                                    <TableCell>
                                      <div>
                                        <p className="font-medium">{format(new Date(doc.expiryDate), 'MMM dd, yyyy')}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {isExpired ? `Expired ${Math.abs(daysUntilExpiry)} days ago` : 
                                           daysUntilExpiry <= 30 ? `Expires in ${daysUntilExpiry} days` : 
                                           'Valid'}
                                        </p>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      {isExpired ? (
                                        <Badge variant="destructive">Expired</Badge>
                                      ) : daysUntilExpiry <= 30 ? (
                                        <Badge variant="secondary">Expiring Soon</Badge>
                                      ) : (
                                        <Badge variant="default">Valid</Badge>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex gap-1">
                                        <Button variant="ghost" size="sm" onClick={() => handleViewDocument(doc)}>
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => handleDownloadDocument(doc)}>
                                          <Download className="h-4 w-4" />
                                        </Button>
                                        <Button 
                                          variant="ghost" 
                                          size="sm" 
                                          onClick={() => {
                                            setSelectedVisitor(visitor || null);
                                            setSelectedDocumentType(doc.documentType);
                                            setShowDocumentUpload(true);
                                          }}
                                        >
                                          <Upload className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => handleDeleteDocument(doc)}>
                                          <Trash2 className="h-4 w-4 text-red-500" />
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                          {typeDocuments.length > 5 && (
                            <p className="text-sm text-muted-foreground mt-2">
                              +{typeDocuments.length - 5} more documents of this type
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>
              
              <TabsContent value="expiry-alerts" className="space-y-4">
                <div className="grid gap-4">
                  {expiredDocuments.length > 0 && (
                    <Card className="border-red-200 bg-red-50">
                      <CardHeader>
                        <CardTitle className="text-red-700 flex items-center gap-2">
                          <AlertCircle className="h-5 w-5" />
                          Expired Documents Requiring Immediate Action
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Visitor</TableHead>
                              <TableHead>Document Type</TableHead>
                              <TableHead>Expired Date</TableHead>
                              <TableHead>Days Overdue</TableHead>
                              <TableHead>Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {expiredDocuments.map((doc) => {
                              const visitor = visitors.find(v => v.id === doc.visitorId);
                              const daysOverdue = Math.ceil((new Date().getTime() - new Date(doc.expiryDate).getTime()) / (1000 * 60 * 60 * 24));
                              
                              return (
                                <TableRow key={doc.id}>
                                  <TableCell className="font-medium">{visitor?.fullName}</TableCell>
                                  <TableCell className="capitalize">{doc.documentType.replace('_', ' ')}</TableCell>
                                  <TableCell>{format(new Date(doc.expiryDate), 'MMM dd, yyyy')}</TableCell>
                                  <TableCell className="text-red-600 font-medium">{daysOverdue} days</TableCell>
                                  <TableCell>
                                    <Button 
                                      size="sm" 
                                      onClick={() => {
                                        setSelectedVisitor(visitor || null);
                                        setSelectedDocumentType(doc.documentType);
                                        setShowDocumentUpload(true);
                                      }}
                                    >
                                      <Upload className="h-4 w-4 mr-2" />
                                      Replace Document
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                  
                  {expiringDocuments.length > 0 && (
                    <Card className="border-yellow-200 bg-yellow-50">
                      <CardHeader>
                        <CardTitle className="text-yellow-700 flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5" />
                          Documents Expiring Within 30 Days
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Visitor</TableHead>
                              <TableHead>Document Type</TableHead>
                              <TableHead>Expiry Date</TableHead>
                              <TableHead>Days Remaining</TableHead>
                              <TableHead>Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {expiringDocuments.map((doc) => {
                              const visitor = visitors.find(v => v.id === doc.visitorId);
                              const daysRemaining = Math.ceil((new Date(doc.expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                              
                              return (
                                <TableRow key={doc.id}>
                                  <TableCell className="font-medium">{visitor?.fullName}</TableCell>
                                  <TableCell className="capitalize">{doc.documentType.replace('_', ' ')}</TableCell>
                                  <TableCell>{format(new Date(doc.expiryDate), 'MMM dd, yyyy')}</TableCell>
                                  <TableCell className="text-yellow-600 font-medium">{daysRemaining} days</TableCell>
                                  <TableCell>
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      onClick={() => {
                                        setSelectedVisitor(visitor || null);
                                        setSelectedDocumentType(doc.documentType);
                                        setShowDocumentUpload(true);
                                      }}
                                    >
                                      <Upload className="h-4 w-4 mr-2" />
                                      Update Document
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Visitor Compliance Table */}
        <Card>
          <CardHeader>
            <CardTitle>Visitor Compliance Status</CardTitle>
            <CardDescription>Document status and compliance tracking for all visitors</CardDescription>
          </CardHeader>
          <CardContent>
            {visitorsLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Full Name</TableHead>
                    <TableHead>ID / Passport</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Compliance Status</TableHead>
                    <TableHead>Last Check</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVisitors.map((visitor) => (
                    <TableRow key={visitor.id} data-testid={`row-visitor-${visitor.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{visitor.fullName}</p>
                            <p className="text-xs text-muted-foreground">{visitor.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {visitor.idType}: {visitor.identityNumber}
                      </TableCell>
                      <TableCell>{visitor.company || 'N/A'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(visitor.complianceStatus)}
                          {getStatusBadge(visitor.complianceStatus)}
                          {visitor.isRevoked && (
                            <p className="text-xs text-red-600 mt-1">
                              Until: {visitor.revokedUntil ? format(new Date(visitor.revokedUntil), 'MMM dd, yyyy') : 'N/A'}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {visitor.lastComplianceCheck 
                          ? format(new Date(visitor.lastComplianceCheck), 'MMM dd, yyyy HH:mm')
                          : 'Never'
                        }
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedVisitorForDetails(visitor);
                              setShowVisitorDetails(true);
                            }}
                            data-testid={`button-view-${visitor.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedVisitor(visitor);
                              setShowDocumentUpload(true);
                            }}
                            data-testid={`button-upload-${visitor.id}`}
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            Upload
                          </Button>
                          
                          {visitor.isRevoked ? (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => reinstateAccessMutation.mutate(visitor.id)}
                              disabled={reinstateAccessMutation.isPending}
                              data-testid={`button-reinstate-${visitor.id}`}
                            >
                              Reinstate
                            </Button>
                          ) : (
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button 
                                  variant="destructive" 
                                  size="sm"
                                  onClick={() => setSelectedVisitor(visitor)}
                                  data-testid={`button-revoke-${visitor.id}`}
                                >
                                  <UserX className="w-4 h-4 mr-1" />
                                  Revoke
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Revoke Access</DialogTitle>
                                  <DialogDescription>
                                    Revoke access for {visitor.fullName}. This will suspend all certifications and access privileges.
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div className="space-y-2">
                                    <Label htmlFor="reason">Reason for Revocation</Label>
                                    <Textarea
                                      id="reason"
                                      placeholder="Enter reason for revoking access..."
                                      value={revokeReason}
                                      onChange={(e) => setRevokeReason(e.target.value)}
                                      data-testid="textarea-revoke-reason"
                                    />
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label htmlFor="period">Period</Label>
                                      <Input
                                        id="period"
                                        type="number"
                                        min="1"
                                        placeholder="Enter number"
                                        value={revokePeriod}
                                        onChange={(e) => setRevokePeriod(e.target.value)}
                                        data-testid="input-revoke-period"
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor="unit">Time Unit</Label>
                                      <Select value={revokeUnit} onValueChange={setRevokeUnit}>
                                        <SelectTrigger data-testid="select-time-unit">
                                          <SelectValue placeholder="Select unit" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="days">Days</SelectItem>
                                          <SelectItem value="weeks">Weeks</SelectItem>
                                          <SelectItem value="months">Months</SelectItem>
                                          <SelectItem value="years">Years</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                </div>
                                <DialogFooter>
                                  <Button variant="outline" onClick={() => setSelectedVisitor(null)}>
                                    Cancel
                                  </Button>
                                  <Button 
                                    variant="destructive" 
                                    onClick={handleRevoke}
                                    disabled={!revokePeriod || !revokeUnit || !revokeReason || revokeAccessMutation.isPending}
                                    data-testid="button-confirm-revoke"
                                  >
                                    Revoke Access
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredVisitors.length === 0 && !visitorsLoading && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No visitors found matching your criteria
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add Visitor Dialog */}
        <Dialog open={showAddVisitor} onOpenChange={setShowAddVisitor}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Visitor</DialogTitle>
              <DialogDescription>
                Create a new visitor profile for document compliance tracking
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input
                    id="fullName"
                    placeholder="Enter full name"
                    value={newVisitorData.fullName}
                    onChange={(e) => setNewVisitorData({ ...newVisitorData, fullName: e.target.value })}
                    data-testid="input-visitor-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="idType">ID Type</Label>
                  <Select 
                    value={newVisitorData.idType} 
                    onValueChange={(value) => setNewVisitorData({ ...newVisitorData, idType: value })}
                  >
                    <SelectTrigger data-testid="select-id-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="id">National ID</SelectItem>
                      <SelectItem value="passport">Passport</SelectItem>
                      <SelectItem value="license">Driver's License</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="idNumber">ID/Passport Number *</Label>
                  <Input
                    id="idNumber"
                    placeholder="Enter ID or passport number"
                    value={newVisitorData.identityNumber}
                    onChange={(e) => setNewVisitorData({ ...newVisitorData, identityNumber: e.target.value })}
                    data-testid="input-visitor-id"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accessLevel">Access Level</Label>
                  <Select 
                    value={newVisitorData.accessLevel} 
                    onValueChange={(value) => setNewVisitorData({ ...newVisitorData, accessLevel: value })}
                  >
                    <SelectTrigger data-testid="select-access-level">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">Basic</SelectItem>
                      <SelectItem value="restricted">Restricted</SelectItem>
                      <SelectItem value="contractor">Contractor</SelectItem>
                      <SelectItem value="vip">VIP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter email address"
                    value={newVisitorData.email}
                    onChange={(e) => setNewVisitorData({ ...newVisitorData, email: e.target.value })}
                    data-testid="input-visitor-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    placeholder="Enter phone number"
                    value={newVisitorData.phone}
                    onChange={(e) => setNewVisitorData({ ...newVisitorData, phone: e.target.value })}
                    data-testid="input-visitor-phone"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  placeholder="Enter company name"
                  value={newVisitorData.company}
                  onChange={(e) => setNewVisitorData({ ...newVisitorData, company: e.target.value })}
                  data-testid="input-visitor-company"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddVisitor(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  if (!newVisitorData.fullName || !newVisitorData.identityNumber) {
                    toast({ 
                      title: "Validation Error", 
                      description: "Full name and ID number are required", 
                      variant: "destructive" 
                    });
                    return;
                  }
                  createVisitorMutation.mutate(newVisitorData);
                }}
                disabled={createVisitorMutation.isPending}
                data-testid="button-create-visitor"
              >
                {createVisitorMutation.isPending ? "Creating..." : "Create Visitor"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Visitor Details Dialog */}
        <Dialog open={showVisitorDetails} onOpenChange={setShowVisitorDetails}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Visitor Details - {selectedVisitorForDetails?.fullName}</DialogTitle>
              <DialogDescription>
                View and manage documents for this visitor
              </DialogDescription>
            </DialogHeader>
            
            {selectedVisitorForDetails && (
              <Tabs defaultValue="documents" className="w-full">
                <TabsList>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                  <TabsTrigger value="compliance">Compliance History</TabsTrigger>
                </TabsList>
                
                <TabsContent value="documents" className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Documents</h3>
                    <Button
                      onClick={() => {
                        setSelectedVisitor(selectedVisitorForDetails);
                        setShowDocumentUpload(true);
                        setShowVisitorDetails(false);
                      }}
                      data-testid="button-add-document"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Document
                    </Button>
                  </div>
                  
                  {documentsLoading ? (
                    <div className="space-y-2">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} className="h-16 w-full" />
                      ))}
                    </div>
                  ) : visitorDocuments.length > 0 ? (
                    <div className="space-y-2">
                      {visitorDocuments.map((doc) => (
                        <Card key={doc.id}>
                          <CardContent className="pt-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <FileText className="h-8 w-8 text-blue-500" />
                                <div>
                                  <p className="font-medium">{doc.fileName}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {doc.documentType.replace('_', ' ').toUpperCase()} • 
                                    Expires: {format(new Date(doc.expiryDate), 'MMM dd, yyyy')}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1">
                                    {getStatusBadge(doc.status)}
                                    <Badge variant="outline" className="text-xs">
                                      {doc.verificationStatus}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.open(doc.fileUrl, '_blank')}
                                  data-testid={`button-view-document-${doc.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  data-testid={`button-download-document-${doc.id}`}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  data-testid={`button-delete-document-${doc.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No documents uploaded yet</p>
                      <Button
                        variant="outline"
                        className="mt-2"
                        onClick={() => {
                          setSelectedVisitor(selectedVisitorForDetails);
                          setShowDocumentUpload(true);
                          setShowVisitorDetails(false);
                        }}
                      >
                        Upload First Document
                      </Button>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="compliance" className="space-y-4">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Card>
                        <CardContent className="pt-4">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(selectedVisitorForDetails.complianceStatus)}
                            <div>
                              <p className="font-medium">Current Status</p>
                              {getStatusBadge(selectedVisitorForDetails.complianceStatus)}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <p className="font-medium">Last Check</p>
                              <p className="text-sm text-muted-foreground">
                                {selectedVisitorForDetails.lastComplianceCheck 
                                  ? format(new Date(selectedVisitorForDetails.lastComplianceCheck), 'MMM dd, yyyy HH:mm')
                                  : 'Never'
                                }
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          queryClient.invalidateQueries({ 
                            queryKey: ['/api/visitors', selectedVisitorForDetails.id, 'compliance'] 
                          });
                          toast({ title: "Success", description: "Compliance status refreshed" });
                        }}
                        data-testid="button-recheck-compliance"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Recheck Compliance
                      </Button>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        {/* Document Upload Dialog */}
        <Dialog open={showDocumentUpload} onOpenChange={setShowDocumentUpload}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
              <DialogDescription>
                Upload a new document for {selectedVisitor?.fullName}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="documentType">Document Type</Label>
                <Select value={selectedDocumentType} onValueChange={setSelectedDocumentType}>
                  <SelectTrigger data-testid="select-document-type">
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="license">Driver's License</SelectItem>
                    <SelectItem value="passport">Passport</SelectItem>
                    <SelectItem value="id">National ID</SelectItem>
                    <SelectItem value="induction">Induction Certificate</SelectItem>
                    <SelectItem value="pdp">PDP Certificate</SelectItem>
                    <SelectItem value="medical">Medical Certificate</SelectItem>
                    <SelectItem value="safety_cert">Safety Certificate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="file">Document File</Label>
                <Input
                  id="file"
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
                  data-testid="input-document-file"
                />
                <p className="text-xs text-muted-foreground">
                  Supported formats: PDF, JPG, PNG. Max size: 10MB
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="issuedDate">Issued Date (Optional)</Label>
                  <Input
                    id="issuedDate"
                    type="date"
                    value={issuedDate}
                    onChange={(e) => setIssuedDate(e.target.value)}
                    data-testid="input-issued-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expiryDate">Expiry Date</Label>
                  <Input
                    id="expiryDate"
                    type="date"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    data-testid="input-expiry-date"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDocumentUpload(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleDocumentUpload}
                disabled={!selectedDocumentType || !documentFile || !expiryDate || uploadDocumentMutation.isPending}
                data-testid="button-upload-document"
              >
                {uploadDocumentMutation.isPending ? "Uploading..." : "Upload Document"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}